import { Button, styled, Typography } from "@material-ui/core";
import { useState } from "react";
import {
  EPOCUS_AMBER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../epocus-palette";
import {
  ViewChecklist,
  WorksheetSection,
} from "../worksheets/worksheet-primitives";
import type { BillingLine } from "./billing-lines";

export const billingCodeLabel = (
  cpt: string,
  modifier: string | null,
  sideModifier: string | null,
): string => `${cpt}${modifier ?? ""}${sideModifier ?? ""}`;

const Hint = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const Warning = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_AMBER,
});

const RevealButton = styled(Button)({
  alignSelf: "flex-start",
  color: EPOCUS_PRIMARY,
  textTransform: "none",
  fontSize: rem(14),
  padding: "4px 0",
  minWidth: 0,
});

const toViews = (
  lines: readonly BillingLine[],
  modifierFor: (cpt: string) => string | null,
) =>
  lines.map((line) => ({
    key: line.key,
    label: `${billingCodeLabel(line.cpt, line.modifier === undefined ? modifierFor(line.cpt) : line.modifier, line.sideModifier)}${line.units > 1 ? ` × ${line.units}` : ""} · ${line.label}`,
  }));

export const BillingSection: FC<{
  lines: readonly BillingLine[];
  otherLines: readonly BillingLine[];
  ticked: Record<string, boolean>;
  onToggle: (line: BillingLine, ticked: boolean) => void;
  modifierFor: (cpt: string) => string | null;
  readOnly: boolean;
  notSetUpMessage: string | null;
}> = ({
  lines,
  otherLines,
  ticked,
  onToggle,
  modifierFor,
  readOnly,
  notSetUpMessage,
}) => {
  const [showOther, setShowOther] = useState(false);
  const shownLines = [
    ...lines,
    ...otherLines.filter((line) => ticked[line.key]),
  ];
  const untickedOther = otherLines.filter((line) => !ticked[line.key]);
  const notes = [...new Set(lines.flatMap((line) => line.notes))];
  const toggle =
    (from: readonly BillingLine[]) => (key: string, checked: boolean) => {
      const line = from.find((candidate) => candidate.key === key);
      if (line) onToggle(line, checked);
    };

  return (
    <WorksheetSection label="Billing">
      <Hint>Signing confirms the ticked codes.</Hint>
      {!readOnly && notSetUpMessage && <Warning>{notSetUpMessage}</Warning>}
      <ViewChecklist
        views={toViews(shownLines, modifierFor)}
        checked={ticked}
        onChange={toggle(shownLines)}
        disabled={readOnly}
      />
      {!readOnly && notes.map((note) => <Hint key={note}>{note}</Hint>)}
      {!readOnly && untickedOther.length > 0 && (
        <>
          <RevealButton
            aria-expanded={showOther}
            onClick={() => setShowOther((open) => !open)}
          >
            {showOther ? "Hide other codes" : "Other codes"}
          </RevealButton>
          {showOther && (
            <ViewChecklist
              views={toViews(untickedOther, modifierFor)}
              checked={ticked}
              onChange={toggle(untickedOther)}
            />
          )}
        </>
      )}
    </WorksheetSection>
  );
};
